import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 * @property {HTMLElement} [independentSizeStatus] - Status for independent TOP/CALCINHA selection.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    if (this.requiresIndependentSizes) {
      this.#setIndependentProductFormPending();

      const selectionState = this.#getIndependentSelectionState();
      if (!selectionState.configured || selectionState.missing.length > 0) {
        this.#updateIndependentSelectionStatus(selectionState);
        this.#removeVariantFromUrl();
        return;
      }

      this.#setIndependentStatus('Verificando a disponibilidade desta combinação…');
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    let morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // A Featured product section normally replaces its entire DOM. For modular
    // TOP/CALCINHA products that would erase the two explicit choices because
    // Liquid intentionally renders both controls unselected. Keep the picker in
    // place and let the native ProductSelectEvent listeners update the verified
    // variant, price, media and add-to-cart state from the returned HTML.
    if (this.requiresIndependentSizes && morphElementSelector === 'featured-product-information') {
      morphElementSelector = undefined;
    }

    const { optionValueId = '', connectedProductUrl = '' } = selectedOption.dataset;
    const variantId = this.requiresIndependentSizes ? '' : selectedOption.dataset.variantId ?? '';
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /** Whether this product requires explicit TOP and CALCINHA selections. */
  get requiresIndependentSizes() {
    return this.dataset.requiresIndependentSizes === 'true';
  }

  /**
   * Returns the explicit independent-size selection state without inferring a fallback variant.
   * @returns {{configured: boolean, missing: string[], values: Record<string, string>}}
   */
  #getIndependentSelectionState() {
    /** @type {Record<string, string>} */
    const values = {};
    const controls = Array.from(this.querySelectorAll('[data-independent-size-part]'));

    for (const control of controls) {
      if (!(control instanceof HTMLElement)) continue;
      const part = control.dataset.independentSizePart;
      if (!part) continue;

      const radio = control.querySelector('input:checked');
      const select = control.querySelector('select');
      if (radio instanceof HTMLInputElement) values[part] = radio.value;
      if (select instanceof HTMLSelectElement && select.value) values[part] = select.value;
    }

    const configured = ['top', 'calcinha'].every((part) =>
      controls.some(
        (control) => control instanceof HTMLElement && control.dataset.independentSizePart === part
      )
    );
    const missing = ['top', 'calcinha'].filter((part) => !values[part]);

    return { configured, missing, values };
  }

  /** @param {string} message */
  #setIndependentStatus(message) {
    if (this.refs.independentSizeStatus) this.refs.independentSizeStatus.textContent = message;
  }

  /** @param {{configured: boolean, missing: string[]}} selectionState */
  #updateIndependentSelectionStatus(selectionState) {
    if (!selectionState.configured) {
      this.#setIndependentStatus('Este conjunto precisa das opções TOP e CALCINHA configuradas.');
      return;
    }

    if (selectionState.missing.length === 2) {
      this.#setIndependentStatus('Escolha os tamanhos de TOP e CALCINHA.');
    } else if (selectionState.missing[0] === 'top') {
      this.#setIndependentStatus('Agora escolha o tamanho do TOP.');
    } else if (selectionState.missing[0] === 'calcinha') {
      this.#setIndependentStatus('Agora escolha o tamanho da CALCINHA.');
    }
  }

  /**
   * Clears the resolved variant and blocks every matching native product form while a new
   * independent-size combination is incomplete or being verified.
   */
  #setIndependentProductFormPending() {
    const scope = this.closest('.shopify-section, dialog') ?? document;
    const forms = scope.querySelectorAll('product-form-component');

    for (const form of forms) {
      if (!(form instanceof HTMLElement) || form.dataset.productId !== this.dataset.productId) continue;

      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement) variantInput.value = '';

      const addButton = form.querySelector('[ref="addToCartButton"]');
      if (addButton instanceof HTMLButtonElement) addButton.disabled = true;

      const acceleratedCheckout = form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (acceleratedCheckout instanceof HTMLElement) acceleratedCheckout.hidden = true;
    }
  }

  #removeVariantFromUrl() {
    if (this.dataset.templateProductMatch !== 'true') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('variant');
    if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
  }

  /** @param {string | number} variantId */
  #setIndependentVariantUrl(variantId) {
    if (this.dataset.templateProductMatch !== 'true') return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();
    const selectedOptionValues = selectedOptions.map(({ value }) => value);

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          if (this.requiresIndependentSizes) {
            this.#setIndependentStatus(
              'Não foi possível validar esta combinação. Tente novamente; suas escolhas foram mantidas.'
            );
          }
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let variantData;
        try {
          variantData = JSON.parse(textContent);
        } catch (error) {
          console.error('[variant-picker] Invalid variant JSON response', error);
          variantData = null;
        }

        if (
          this.requiresIndependentSizes &&
          !this.#variantMatchesExplicitSelection(variantData, selectedOptionValues)
        ) {
          this.#disableProductFormInResponse(html);
          this.#setIndependentStatus('Esta combinação de tamanhos está indisponível. Suas escolhas foram mantidas.');
          this.#removeVariantFromUrl();
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: optionValueId,
              resource: null,
            },
          });
          return;
        }

        if (this.requiresIndependentSizes) {
          this.#prepareIndependentProductFormResponse(html, variantData);
        }

        let newProduct;
        const resolvedSourceId = this.requiresIndependentSizes ? optionValueId : this.selectedOptionId;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else if (!this.requiresIndependentSizes) {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (resolvedSourceId) {
          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: resolvedSourceId,
                resource: variantData,
              },
            });

            if (this.requiresIndependentSizes) {
              this.#setIndependentVariantUrl(variantData.id);
              if (variantData.available) {
                this.#setIndependentStatus('Tamanhos de TOP e CALCINHA selecionados.');
              } else {
                this.#setIndependentStatus(
                  'Esta combinação de tamanhos está indisponível. Suas escolhas foram mantidas.'
                );
              }
            }

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: resolvedSourceId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
          if (this.requiresIndependentSizes) {
            this.#setIndependentStatus(
              'Não foi possível verificar a combinação. Tente novamente; suas escolhas foram mantidas.'
            );
          }
        }
      });
  }

  /**
   * Ensures a server response represents exactly the values explicitly selected by the shopper.
   * @param {unknown} variantData
   * @param {string[]} selectedOptionValues
   * @returns {boolean}
   */
  #variantMatchesExplicitSelection(variantData, selectedOptionValues) {
    if (!variantData || typeof variantData !== 'object') return false;
    const responseOptions = /** @type {{options?: unknown}} */ (variantData).options;
    if (!Array.isArray(responseOptions) || responseOptions.length !== selectedOptionValues.length) return false;

    return responseOptions.every((value, index) => String(value) === selectedOptionValues[index]);
  }

  /** @param {Document} html */
  #disableProductFormInResponse(html) {
    const variantInput = html.querySelector('product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = '';

    const addButton = html.querySelector('product-form-component [ref="addToCartButton"]');
    if (addButton instanceof HTMLButtonElement) addButton.disabled = true;

    const acceleratedCheckout = html.querySelector(
      'product-form-component [ref="acceleratedCheckoutButtonContainer"]'
    );
    if (acceleratedCheckout instanceof HTMLElement) acceleratedCheckout.hidden = true;
  }

  /**
   * Makes the fetched native form reflect the exact verified combination before its normal
   * ProductSelectEvent listener morphs the button.
   * @param {Document} html
   * @param {{id?: string | number, available?: boolean}} variantData
   */
  #prepareIndependentProductFormResponse(html, variantData) {
    const isAvailable = variantData.available === true && variantData.id != null;
    const variantInput = html.querySelector('product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = isAvailable ? String(variantData.id) : '';
    }

    const addButton = html.querySelector('product-form-component [ref="addToCartButton"]');
    if (!(addButton instanceof HTMLButtonElement)) return;

    addButton.disabled = !isAvailable;
    addButton.setAttribute(
      'aria-label',
      isAvailable ? 'Adicionar à sacola' : 'Combinação de tamanhos indisponível'
    );

    for (const node of addButton.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        node.textContent = isAvailable ? ' Adicionar à sacola ' : ' Combinação indisponível ';
        break;
      }
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    for (const selected of this.#getSelectedOptionElementsInOrder()) {
      if (selected.dataset.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    return options;
  }

  /**
   * Gets selected option inputs in product-option order.
   * @returns {(HTMLInputElement | HTMLOptionElement)[]}
   */
  #getSelectedOptionElementsInOrder() {
    /** @type {(HTMLInputElement | HTMLOptionElement)[]} */
    const selectedElements = [];
    const controls = Array.from(this.querySelectorAll('[data-product-option-position]')).sort(
      (a, b) =>
        Number(a.getAttribute('data-product-option-position')) - Number(b.getAttribute('data-product-option-position'))
    );

    for (const control of controls) {
      const checkedInput = control.querySelector('input:checked, input[data-single-option]');
      if (checkedInput instanceof HTMLInputElement) {
        selectedElements.push(checkedInput);
        continue;
      }

      const select = control.querySelector('select');
      const selectedOption = select instanceof HTMLSelectElement ? select.selectedOptions[0] : null;
      if (selectedOption instanceof HTMLOptionElement && selectedOption.value) selectedElements.push(selectedOption);
    }

    if (controls.length === 0) {
      const fallbackSelected = this.querySelectorAll('select option[selected], fieldset input:checked');
      for (const option of fallbackSelected) {
        if (option instanceof HTMLInputElement || option instanceof HTMLOptionElement) {
          selectedElements.push(option);
        }
      }
    }

    return selectedElements;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    return this.#getSelectedOptionElementsInOrder().map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
